import React, { useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF, Html, useProgress } from '@react-three/drei';
import * as THREE from 'three';

function Model({ hidden, hiddenMeshes, isMobile }) {
  const { scene } = useGLTF('/AirTent_V3.glb');

  React.useEffect(() => {
    const meshesToAnimate = [];
    scene.traverse((child) => {
      if (child.isMesh) {
        if (child.material) {
          child.material.side = THREE.DoubleSide;
          child.material.transparent = true;
          child.material.needsUpdate = true;
        }

        if (child.name === 'Low_InnerPocket_1') {
          if (child.material) {
            child.material.side = THREE.DoubleSide;
            child.material.transparent = true;
            child.material.opacity = 1;
            child.material.depthWrite = true;
            child.material.depthTest = true;
            child.material.needsUpdate = true;
          }

          const geometry = child.geometry;
          if (geometry) {
            geometry.computeBoundingSphere();
            geometry.computeBoundingBox();
            geometry.computeVertexNormals();
            geometry.attributes.position.needsUpdate = true;
            geometry.normalsNeedUpdate = true;
            child.visible = true;
          }

          child.renderOrder = 1;
        }

        if (hiddenMeshes.includes(child.name)) {
          meshesToAnimate.push(child);
        }
      }
    });

    const animateOpacity = () => {
      meshesToAnimate.forEach((mesh) => {
        if (hidden) {
          mesh.material.opacity = Math.max(mesh.material.opacity - 0.05, 0);
          if (mesh.material.opacity === 0) mesh.visible = false;
        } else {
          mesh.visible = true;
          mesh.material.opacity = Math.min(mesh.material.opacity + 0.05, 1);
        }
      });

      if (
        meshesToAnimate.some(
          (mesh) =>
            (hidden && mesh.material.opacity > 0) || (!hidden && mesh.material.opacity < 1)
        )
      ) {
        requestAnimationFrame(animateOpacity);
      }
    };

    animateOpacity();
  }, [hidden, hiddenMeshes, scene]);

  return (
    <primitive
      object={scene}
      position={[0, 0, 0]}
      scale={isMobile ? [0.005, 0.005, 0.005] : [0.01, 0.01, 0.01]} // Adjust scale for mobile
    />
  );
}

function Configurator() {
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent); // Mobile detection
  const hiddenMeshes = [
    'Low_Cover_base_1',
    'Low_Curtern_Back_1',
    'Low_Curtain_Front_1',
    'Low_Tent_Hood_Cover_Front_1',
    'Low_Cover_Post_1',
    'Low_Hood_Stick_1',
    'Low_Tent_Cover_Hood_1',
    'Low_Tent_Hood_Cover_Sides_1',
  ];

  const toggleVisibility = () => setHidden((prev) => !prev);

  const { progress } = useProgress();
  React.useEffect(() => {
    if (progress === 100) {
      setLoading(false);
    }
  }, [progress]);

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
        background: '#ffffff',
        overflow: 'hidden',
      }}
    >
      {loading && <LoadingBar />}
      <Canvas
        style={{ flex: 1 }}
        camera={{ position: [25, 25, 25], fov: 75 }}
        onCreated={({ gl }) => {
          gl.setClearColor(new THREE.Color('#ffffff'));
        }}
      >
        <ambientLight intensity={2.8} />
        <directionalLight position={[5, 5, 5]} intensity={1.5} />
        <Model hidden={hidden} hiddenMeshes={hiddenMeshes} isMobile={isMobile} />
        <OrbitControls enableZoom={false} enablePan={false} makeDefault />
        <Html
          position={isMobile ? [0, 5.25, 7.75] : [0, 10.5, 15.5]} // Adjust position for mobile
          distanceFactor={isMobile ? 15 : 30} // Adjust distanceFactor for mobile
          occlude
          onOcclude={(isOccluded) => {
            const element = document.querySelector('.annotation');
            if (element) {
              element.style.opacity = isOccluded ? '0.3' : '1';
            }
          }}
        >
          <div className="annotation">
            <button onClick={toggleVisibility}>
              {hidden ? 'Hide Inner Tent' : 'Show Inner Tent'}
            </button>
          </div>
        </Html>
      </Canvas>
    </div>
  );
}

function LoadingBar() {
  const { progress } = useProgress(); // Get progress from useProgress

  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        color: '#fff',
        padding: '20px',
        borderRadius: '5px',
        zIndex: 10,
        textAlign: 'center',
      }}
    >
      <p>Loading... {Math.round(progress)}%</p>
      <div
        style={{
          width: '200px',
          height: '10px',
          backgroundColor: '#ccc',
          borderRadius: '5px',
          overflow: 'hidden',
          marginTop: '10px',
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: '100%',
            backgroundColor: '#007BFF',
          }}
        ></div>
      </div>
    </div>
  );
}

export default Configurator;
